import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { personAPI } from '../services/api';
import PersonList from '../components/PersonList';
import PersonDetails from '../components/PersonDetails';
import CreatePersonModal from '../components/CreatePersonModal';
import ImportModal from '../components/ImportModal';
import OverviewPanel from '../components/OverviewPanel';

// Birth dates are stored in European DD.MM.YYYY format. Parse that explicitly
// so ambiguous dates (e.g. 07.04.2002) aren't misread as American MM.DD.YYYY.
const parseBirthMonthDay = (birthDate) => {
  if (!birthDate) return null;
  const cleaned = birthDate.trim();
  // DD.MM.YYYY or DD/MM/YYYY (stored format)
  const euroMatch = cleaned.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
  if (euroMatch) {
    const day = parseInt(euroMatch[1], 10);
    const month = parseInt(euroMatch[2], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { month: month - 1, day };
    }
  }
  // Fallback: let the engine try (handles ISO YYYY-MM-DD etc.)
  const date = new Date(cleaned);
  if (!isNaN(date.getTime())) {
    return { month: date.getMonth(), day: date.getDate() };
  }
  return null;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchPersons();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchPersons = async () => {
    try {
      const response = await personAPI.getAll();
      setPersons(response.data);
    } catch (error) {
      console.error('Error fetching persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = async (personId) => {
    try {
      // Update last_accessed
      await personAPI.update(personId, { last_accessed: new Date().toISOString() });
      // Fetch person details
      const response = await personAPI.getById(personId);
      setSelectedPerson(response.data);
      setShowOverview(false);
      // Refresh persons list so recent list updates
      const allPersons = await personAPI.getAll();
      setPersons(allPersons.data);
    } catch (error) {
      console.error('Error fetching person details:', error);
    }
  };

  const handlePersonCreated = (newPerson) => {
    setPersons([...persons, newPerson]);
    setShowCreateModal(false);
    handlePersonSelect(newPerson.id);
  };

  const handlePersonUpdated = (updatedPerson) => {
    setPersons(persons.map(p => p.id === updatedPerson.id ? updatedPerson : p));
    setSelectedPerson(updatedPerson);
  };

  const handlePersonDeleted = (personId) => {
    setPersons(persons.filter(p => p.id !== personId));
    setSelectedPerson(null);
    setShowOverview(true);
  };

  const handleImportSuccess = () => {
    fetchPersons();
    setShowImportModal(false);
  };

  const filteredPersons = persons.filter(person =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcomingBirthdays = persons.filter(p => {
      const parsed = parseBirthMonthDay(p.birthDate);
      if (!parsed) return false;
      const thisYear = new Date(today.getFullYear(), parsed.month, parsed.day);
      const diffDays = Math.ceil((thisYear - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 30;
    });
    const withAddress = persons.filter(p => p.street || p.city).length;
    return {
      total: persons.length,
      birthdays: upcomingBirthdays.length,
      withLocation: withAddress
    };
  }, [persons]);

  return (
    <div className="flex h-full bg-tech-bg text-tech-text overflow-hidden">
      {/* Left Sidebar - Collapsible */}
      <aside
        className={`${
          sidebarCollapsed ? 'w-16' : 'w-72'
        } bg-sidebar-bg border-r border-tech-border flex flex-col transition-all duration-300 relative z-20`}
      >
        {/* Header */}
        <div className="p-3 border-b border-tech-border">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-tech-surface border border-tech-border flex items-center justify-center glow-accent-sm">
                  <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.98 10.804V12.18c0 1.128.48 2.11 1.237 2.747l2.963 2.478c.77.646.77 1.882 0 2.528L19.7 21.02c-.757.637-1.237 1.62-1.237 2.748v1.436" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.02 13.196V11.82c0-1.128-.48-2.11-1.237-2.747L.82 6.595c-.77-.646-.77-1.882 0-2.528L4.298 1.88c.757-.637 1.237-1.62 1.237-2.748V.982" />
                  </svg>
                </div>
                <span className="font-mono font-semibold text-sm tracking-tight">XBASE</span>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-tech-textMuted hover:text-tech-text hover:bg-tech-surface rounded transition"
              title={sidebarCollapsed ? 'Expand' : 'Collapse'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7m8 14l-7-7 7-7"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        {!sidebarCollapsed && (
          <div className="px-3 py-2 border-b border-tech-border">
            <div className="flex gap-2">
              <div className="flex-1 tech-card p-2 text-center">
                <p className="text-tech-accent font-mono text-lg font-semibold">{stats.total}</p>
                <p className="text-tech-textDim text-[10px] font-mono uppercase">RECORDS</p>
              </div>
              <div className="flex-1 tech-card p-2 text-center">
                <p className="text-tech-warning font-mono text-lg font-semibold">{stats.birthdays}</p>
                <p className="text-tech-textDim text-[10px] font-mono uppercase">BIRTHDAYS</p>
              </div>
              <div className="flex-1 tech-card p-2 text-center">
                <p className="text-tech-success font-mono text-lg font-semibold">{stats.withLocation}</p>
                <p className="text-tech-textDim text-[10px] font-mono uppercase">MAPPED</p>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="px-3 py-3">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tech-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-tech-surface border border-tech-border rounded text-sm text-tech-text placeholder:text-tech-textMuted focus:outline-none focus:border-tech-accent transition"
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!sidebarCollapsed && (
          <div className="px-3 py-2 space-y-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full tech-btn-primary py-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>NEW RECORD</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="w-full tech-btn-secondary py-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>IMPORT JSON</span>
            </button>
          </div>
        )}

        {/* Collapsed icons */}
        {sidebarCollapsed && (
          <div className="flex flex-col items-center py-2 space-y-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="p-2 text-tech-textMuted hover:text-tech-accent hover:bg-tech-surface rounded transition"
              title="New Record"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="p-2 text-tech-textMuted hover:text-tech-accent hover:bg-tech-surface rounded transition"
              title="Import JSON"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </button>
          </div>
        )}

        {/* Person List */}
        {!sidebarCollapsed ? (
          <PersonList
            persons={filteredPersons}
            selectedPerson={selectedPerson}
            onPersonSelect={handlePersonSelect}
            loading={loading}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center py-2 space-y-2 overflow-y-auto">
            {filteredPersons.slice(0, 20).map((person) => (
              <button
                key={person.id}
                onClick={() => handlePersonSelect(person.id)}
                className={`w-10 h-10 rounded flex items-center justify-center font-mono text-sm font-semibold transition ${
                  selectedPerson?.id === person.id
                    ? 'bg-tech-accent text-tech-bg'
                    : 'bg-tech-surface text-tech-textMuted hover:bg-tech-surface/80 hover:text-tech-text'
                }`}
                title={person.name}
              >
                {person.name.charAt(0).toUpperCase()}
              </button>
            ))}
            {filteredPersons.length > 20 && (
              <span className="text-tech-textDim text-xs">+{filteredPersons.length - 20}</span>
            )}
          </div>
        )}

        {/* User Menu */}
        <div className="p-3 border-t border-tech-border mt-auto">
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-tech-accent/10 border border-tech-accent/30 flex items-center justify-center">
                  <span className="text-tech-accent font-mono text-sm font-semibold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{user?.username}</p>
                  <p className="text-tech-textDim text-xs truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="p-2 text-tech-textMuted hover:text-tech-danger hover:bg-tech-danger/10 rounded transition"
                title="Logout"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              className="w-full p-2 text-tech-textMuted hover:text-tech-danger hover:bg-tech-danger/10 rounded transition"
              title="Logout"
            >
              <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-0">
        {/* Top Bar */}
        <header className="bg-tech-surface border-b border-tech-border px-4 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            {!showOverview && (
              <button
                onClick={() => {
                  setSelectedPerson(null);
                  setShowOverview(true);
                }}
                className="flex items-center gap-2 text-tech-textMuted hover:text-tech-text transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm">BACK</span>
              </button>
            )}
            <div className="flex items-center gap-2">
              <span className="status-dot-active" />
              <h2 className="text-sm font-mono font-medium text-tech-text">
                {showOverview ? 'SYSTEM OVERVIEW' : selectedPerson?.name?.toUpperCase() || 'NO RECORD SELECTED'}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-tech-textDim text-xs font-mono hidden md:block">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }).toUpperCase()}
            </span>
            <span className="text-tech-accent text-xs font-mono">
              {currentTime.toLocaleTimeString('en-US', { hour12: false }).toUpperCase()}
            </span>
            <button
              onClick={toggleTheme}
              className="relative w-14 h-7 rounded-full transition-all duration-300 cursor-pointer"
              style={{ background: 'var(--tech-border)' }}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <div className="absolute top-1 w-5 h-5 rounded-full transition-all duration-300 flex items-center justify-center"
                style={{
                  left: isDarkMode ? '2px' : 'calc(100% - 22px)',
                  background: isDarkMode ? 'var(--tech-accent)' : '#f59e0b',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {isDarkMode ? (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </div>
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto min-h-0 relative">
          {/* Grid Background */}
          <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />
          
          {showOverview ? (
            <OverviewPanel
              persons={persons}
              stats={stats}
              onPersonSelect={(id) => {
                handlePersonSelect(id);
              }}
              onDataRefresh={fetchPersons}
            />
          ) : selectedPerson ? (
            <PersonDetails
              person={selectedPerson}
              onUpdate={handlePersonUpdated}
              onDelete={handlePersonDeleted}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded bg-tech-surface border border-tech-border flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-tech-textDim" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-tech-textMuted text-sm font-mono">NO RECORD SELECTED</p>
                <p className="text-tech-textDim text-xs mt-1">SELECT FROM SIDEBAR OR CREATE NEW</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {showCreateModal && (
        <CreatePersonModal
          onClose={() => setShowCreateModal(false)}
          onPersonCreated={handlePersonCreated}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default Dashboard;
